import { useCallback, useEffect, useRef, useState } from "react";
import { AUTH_EVENT, getActiveSession, SESSION_STORAGE_KEY } from "../../utils/nightStayAccount";
import { emptyDraft, sessionIdentity } from "./conversationModel";

// Drafts live for this tab's app session; raw messages/files never go to storage.
export default function useSupportConversationState() {
  const [accountKey, setAccountKey] = useState(() => sessionIdentity(getActiveSession()));
  const identityRef = useRef(accountKey);
  const [drafts, setDrafts] = useState({});
  const draftsRef = useRef(drafts);
  const [navigation, setNavigationState] = useState({ view: "list", ticketId: "", bookingId: "" });
  const navigationRef = useRef(navigation);
  const setNavigation = useCallback((next) => { navigationRef.current = next; setNavigationState(next); }, []);
  const getNavigation = useCallback(() => navigationRef.current, []);
  const updateDraft = useCallback((key, change, expectedAccount = identityRef.current) => {
    if (expectedAccount !== identityRef.current) return;
    const current = draftsRef.current[key] || emptyDraft();
    const next = typeof change === "function" ? change(current) : { ...current, ...change };
    draftsRef.current = { ...draftsRef.current, [key]: next };
    setDrafts(draftsRef.current);
  }, []);
  const clearDraft = useCallback((key, expectedAccount = identityRef.current) => {
    if (expectedAccount !== identityRef.current) return;
    const next = { ...draftsRef.current }; delete next[key]; draftsRef.current = next; setDrafts(next);
  }, []);
  const getDraft = useCallback((key) => draftsRef.current[key] || emptyDraft(), []);
  useEffect(() => {
    const onAuth = () => {
      const next = sessionIdentity(getActiveSession());
      if (next === identityRef.current) return;
      // Keep an explicitly drafted guest request through login; never transfer
      // one signed-in customer's drafts/history to another account.
      if (identityRef.current) {
        draftsRef.current = {}; setDrafts({});
        setNavigation({ view: "list", ticketId: "", bookingId: "" });
      }
      identityRef.current = next; setAccountKey(next);
    };
    const onStorage = (event) => { if (!event.key || event.key === SESSION_STORAGE_KEY) onAuth(); };
    window.addEventListener(AUTH_EVENT, onAuth); window.addEventListener("storage", onStorage);
    const beforeUnload = (event) => {
      if (Object.values(draftsRef.current).some((draft) => draft.text.trim() || draft.attachments.length || draft.pending)) {
        event.preventDefault(); event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => { window.removeEventListener(AUTH_EVENT, onAuth); window.removeEventListener("storage", onStorage); window.removeEventListener("beforeunload", beforeUnload); };
  }, [setNavigation]);
  return { accountKey, drafts, getDraft, updateDraft, clearDraft, navigation, setNavigation, getNavigation };
}
